/**
 * <store-location>
 * Single-store page: Leaflet + OpenStreetMap map, Nominatim geocoding,
 * OSRM driving directions, and a thumbnail gallery. No Google/Mapbox key.
 */
if (!customElements.get('store-location')) {
	const TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
	const TILE_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
	const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
	const OSRM_URL = 'https://router.project-osrm.org/route/v1/driving/';

	const PIN_SVG =
		'<svg xmlns="http://www.w3.org/2000/svg" width="30" height="38" viewBox="0 0 30 38" fill="currentColor" aria-hidden="true">' +
		'<path d="M15 0C6.716 0 0 6.716 0 15c0 10.5 15 23 15 23s15-12.5 15-23C30 6.716 23.284 0 15 0Z"/>' +
		'<circle cx="15" cy="15" r="5.4" fill="#fff"/></svg>';

	class StoreLocation extends HTMLElement {
		connectedCallback() {
			if (this.initialised) return;
			this.initialised = true;

			this.defaultZoom = parseInt(this.dataset.defaultZoom, 10) || 15;
			this.store = {
				name: this.dataset.name || '',
				address: this.dataset.address || '',
				lat: parseFloat(this.dataset.lat),
				lng: parseFloat(this.dataset.lng),
			};
			if (!Number.isFinite(this.store.lat)) this.store.lat = null;
			if (!Number.isFinite(this.store.lng)) this.store.lng = null;

			this.map = null;
			this.storeMarker = null;
			this.originMarker = null;
			this.routeLayer = null;
			this.handlers = {};

			this.cacheDom();
			this.bindEvents();
			this.initMap();
		}

		disconnectedCallback() {
			this.teardown();
		}

		cacheDom() {
			this.els = {
				mapEl: this.querySelector('[data-store-location-map]'),
				form: this.querySelector('[data-store-location-directions]'),
				fromInput: this.querySelector('[data-store-location-from]'),
				toInput: this.querySelector('[data-store-location-to]'),
				swapBtn: this.querySelector('[data-store-location-swap]'),
				status: this.querySelector('[data-store-location-status]'),
				galleryMain: this.querySelector('.store-location__gallery-main img'),
				thumbs: Array.from(this.querySelectorAll('[data-store-location-thumb]')),
			};
		}

		bindEvents() {
			if (this.els.form) {
				this.handlers.submit = (event) => {
					event.preventDefault();
					this.getDirections();
				};
				this.els.form.addEventListener('submit', this.handlers.submit);
			}

			if (this.els.swapBtn) {
				this.handlers.swap = () => this.swapEnds();
				this.els.swapBtn.addEventListener('click', this.handlers.swap);
			}

			this.els.thumbs.forEach((thumb) => {
				thumb.addEventListener('click', () => this.showImage(thumb));
			});
		}

		teardown() {
			if (this.map) {
				this.map.remove();
				this.map = null;
			}
		}

		whenLeafletReady(callback, attempt = 0) {
			if (window.L) {
				callback(window.L);
			} else if (attempt < 40) {
				setTimeout(() => this.whenLeafletReady(callback, attempt + 1), 100);
			} else {
				console.error('[store-location] Leaflet failed to load');
			}
		}

		initMap() {
			if (!this.els.mapEl || this.store.lat === null || this.store.lng === null) return;
			this.whenLeafletReady((L) => {
				if (!this.isConnected || this.map) return;
				this.map = L.map(this.els.mapEl, {
					scrollWheelZoom: false,
					zoomControl: true,
				}).setView([this.store.lat, this.store.lng], this.defaultZoom);

				L.tileLayer(TILE_URL, { maxZoom: 19, attribution: TILE_ATTRIBUTION }).addTo(this.map);

				this.storeMarker = L.marker([this.store.lat, this.store.lng], {
					icon: this.pinIcon('store'),
					title: this.store.name,
					alt: this.store.name,
				}).addTo(this.map);

				if (this.store.name || this.store.address) {
					let html = '';
					if (this.store.name) html += `<span class="store-location__popup-name">${this.escape(this.store.name)}</span>`;
					if (this.store.address) {
						html += `<p>${this.escape(this.store.address).replace(/\n/g, '<br>')}</p>`;
					}
					this.storeMarker.bindPopup(html, { className: 'store-location__popup', minWidth: 200 });
				}

				requestAnimationFrame(() => this.map && this.map.invalidateSize());
			});
		}

		pinIcon(kind) {
			const extra = kind === 'origin' ? ' store-location__marker--origin' : '';
			return window.L.divIcon({
				className: 'store-location__marker' + extra,
				html: PIN_SVG,
				iconSize: [30, 38],
				iconAnchor: [15, 38],
				popupAnchor: [0, -34],
			});
		}

		swapEnds() {
			if (!this.els.fromInput || !this.els.toInput) return;
			const from = this.els.fromInput.value;
			this.els.fromInput.value = this.els.toInput.value;
			this.els.toInput.value = from;
			this.els.fromInput.focus();
		}

		async getDirections() {
			if (!this.els.fromInput || !this.els.toInput) return;
			const fromQuery = this.els.fromInput.value.trim();
			const toQuery = this.els.toInput.value.trim();

			if (!fromQuery) {
				this.setStatus(this.dataset.originHint || 'Enter a starting location.');
				this.els.fromInput.focus();
				return;
			}

			this.setStatus(this.dataset.searchingText || 'Searching…');

			try {
				const from = await this.resolvePoint(fromQuery);
				const to = await this.resolvePoint(toQuery || this.store.address);
				if (!from || !to) {
					this.setStatus(this.dataset.noResultsText || 'We couldn’t find that location.');
					return;
				}

				this.drawOrigin(from);
				await this.drawRoute(from, to);
			} catch (error) {
				console.error('[store-location] directions failed', error);
				this.setStatus(this.dataset.noResultsText || 'We couldn’t find that location.');
			}
		}

		matchesStore(query) {
			const a = this.normalize(query);
			const b = this.normalize(this.store.address);
			return a && b && (a === b || b.includes(a) || a.includes(b));
		}

		normalize(value) {
			return String(value || '')
				.replace(/\s+/g, ' ')
				.trim()
				.toLowerCase();
		}

		async resolvePoint(query) {
			if (this.matchesStore(query) && this.store.lat !== null && this.store.lng !== null) {
				return { lat: this.store.lat, lng: this.store.lng, label: this.store.name || this.store.address };
			}
			const url = `${NOMINATIM_URL}?format=json&limit=1&q=${encodeURIComponent(query)}`;
			const response = await fetch(url, { headers: { Accept: 'application/json' } });
			const results = await response.json();
			if (!Array.isArray(results) || !results.length) return null;
			const place = results[0];
			return {
				lat: parseFloat(place.lat),
				lng: parseFloat(place.lon),
				label: place.display_name,
			};
		}

		drawOrigin(point) {
			if (!this.map || !window.L) return;
			if (this.originMarker) this.originMarker.remove();
			this.originMarker = window.L.marker([point.lat, point.lng], {
				icon: this.pinIcon('origin'),
				title: point.label || this.dataset.yourLocationLabel || 'Your location',
				alt: point.label || '',
			}).addTo(this.map);
		}

		async drawRoute(from, to) {
			const response = await fetch(
				`${OSRM_URL}${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`
			);
			const data = await response.json();
			if (data.code !== 'Ok' || !data.routes || !data.routes.length) {
				const fallback = `https://www.openstreetmap.org/directions?engine=fossgis_osrm_car&route=${from.lat},${from.lng};${to.lat},${to.lng}`;
				this.setStatusHtml(
					`${this.escape(this.dataset.noResultsText || 'Could not load the route.')} ` +
						`<a href="${fallback}" target="_blank" rel="noopener">OpenStreetMap</a>`
				);
				return;
			}

			const route = data.routes[0];
			this.whenLeafletReady((L) => {
				if (!this.map) return;
				if (this.routeLayer) this.routeLayer.remove();
				this.routeLayer = L.geoJSON(route.geometry, {
					style: { className: 'store-location__path', weight: 4, opacity: 0.85 },
				}).addTo(this.map);
				this.map.fitBounds(this.routeLayer.getBounds().pad(0.18));
			});

			const km = route.distance / 1000;
			const minutes = Math.round(route.duration / 60);
			this.setStatus(`${km.toFixed(1)} km, about ${minutes} min by car.`);
		}

		showImage(thumb) {
			const img = this.els.galleryMain;
			if (!img) return;

			const url = thumb.dataset.fullUrl;
			if (!url) return;

			img.src = url;
			if (thumb.dataset.fullSrcset) img.srcset = thumb.dataset.fullSrcset;
			if (thumb.dataset.fullSizes) img.sizes = thumb.dataset.fullSizes;
			if (thumb.dataset.fullWidth) img.width = thumb.dataset.fullWidth;
			if (thumb.dataset.fullHeight) img.height = thumb.dataset.fullHeight;
			img.alt = thumb.dataset.fullAlt || '';

			this.els.thumbs.forEach((item) => {
				const on = item === thumb;
				item.classList.toggle('is-active', on);
				item.setAttribute('aria-pressed', String(on));
			});
		}

		setStatus(text) {
			if (!this.els.status) return;
			this.els.status.textContent = text || '';
			this.els.status.hidden = !text;
		}

		setStatusHtml(html) {
			if (!this.els.status) return;
			this.els.status.innerHTML = html;
			this.els.status.hidden = !html;
		}

		escape(value) {
			return String(value == null ? '' : value)
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#39;');
		}
	}

	customElements.define('store-location', StoreLocation);
}
